import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Fab from '@mui/material/Fab';
import Stack from '@mui/material/Stack';
import Zoom from '@mui/material/Zoom';
import { useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { CloseIcon, DatasetConfigIcon, EditIcon, OpenIcon } from '@common/utils/icons/icons';
import SphereNavigationRoutes from '@common/enums/query-params/sphere-navigation-routes';
import { FilterKeys } from '@common/enums/query-params/filters';
import NavigationButton from 'components/ui/NavigationButton';
import CustomFleetDashboardIcon from 'components/fleet-management/CustomFleetDashboardIcon';
import FleetDashboardModel, {
  BUILT_IN_PAGES,
  DashboardViewType,
  resolveGeneralPage,
} from 'models/fleet-management/FleetDashboard';
import OrganizationRole from 'models/enums/organization-roles';
import { useAppSelector } from 'store';
import { selectUser } from 'store/general/general-slice-selectors';
import { selectFleetDashboardsData } from 'store/fleet-management/fleet-management-slice-selectors';
import { PageOption } from 'models/enums/fleet-management';

const FleetNavigationBar: React.FC<{
  isNavOpen: boolean;
  onNavChangeHandler: () => void;
  /** Opens the dashboard configuration dialog for the currently selected dashboard. */
  onEditDashboard: () => void;
}> = ({ isNavOpen, onNavChangeHandler, onEditDashboard }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [queryParams] = useSearchParams();

  const matchesSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const currentUser = useAppSelector((state) => selectUser(state));
  const dashboardsData = useAppSelector((state) => selectFleetDashboardsData(state));

  const [isToggleSideBarButtonVisible, setIsToggleSideBarButtonVisible] = useState(false);

  const isManufacturer = currentUser.data.organization.role === OrganizationRole.MANUFACTURER;
  const canSeeDealerOverview = [
    OrganizationRole.MANUFACTURER,
    OrganizationRole.WHOLESALER,
    OrganizationRole.IMPORTER,
  ].includes(currentUser.data.organization.role);

  const internalDashboards =
    dashboardsData.data?.filter((d) => d.viewType === DashboardViewType.DEALER_OVERVIEW) ?? [];
  const dealerDashboards =
    dashboardsData.data?.filter((d) => d.viewType === DashboardViewType.FLEET_OVERVIEW) ?? [];
  // Saved views of the health and performance pages sit with the dealer views: every Insights
  // user can open those pages, so every Insights user sees their saved setups.
  const changeDashboards =
    dashboardsData.data?.filter(
      (d) =>
        d.viewType === DashboardViewType.FLEET_HEALTH ||
        d.viewType === DashboardViewType.FLEET_PERFORMANCE,
    ) ?? [];
  const orbitDashboards =
    dashboardsData.data?.filter((d) => d.viewType === DashboardViewType.ORBIT) ?? [];
  const defaultOrbitDashboards = orbitDashboards.filter((d) => d.isDefault);
  const personalOrbitDashboards = orbitDashboards.filter((d) => !d.isDefault);

  const isDatasetPage = pathname.startsWith(SphereNavigationRoutes.InsightsDatasets);
  const queryDashboardId = queryParams.get(FilterKeys.FleetDashboard);
  const queryDashboardView = queryParams.get(FilterKeys.DashboardView);

  const activeGenericPage =
    isDatasetPage || queryDashboardId != null
      ? null
      : resolveGeneralPage(queryDashboardView, canSeeDealerOverview);

  const builtInPages = BUILT_IN_PAGES.filter(
    (p) => p.page !== PageOption.DealerOverview || canSeeDealerOverview,
  );

  const isExpanded = isNavOpen && !matchesSmDown;

  const closeNavOnSmallScreenHandler = () => {
    if (matchesSmDown && isNavOpen) {
      onNavChangeHandler();
    }
  };

  const genericPageClickHandler = (page: PageOption) => {
    navigate(`${SphereNavigationRoutes.Insights}?${FilterKeys.DashboardView}=${page}`);
    closeNavOnSmallScreenHandler();
  };

  const dashboardClickHandler = (dashboardId: string) => {
    navigate(`${SphereNavigationRoutes.Insights}?${FilterKeys.FleetDashboard}=${dashboardId}`);
    closeNavOnSmallScreenHandler();
  };

  const datasetsClickHandler = () => {
    navigate(SphereNavigationRoutes.InsightsDatasets);
    closeNavOnSmallScreenHandler();
  };

  const editDashboardClickHandler = (dashboardId: string) => {
    dashboardClickHandler(dashboardId);
    onEditDashboard();
  };

  const mouseOverNavHandler = () => {
    setIsToggleSideBarButtonVisible(true);
  };

  const mouseOffNavHandler = () => {
    setIsToggleSideBarButtonVisible(false);
  };

  const toggleSideBarClickHandler = () => {
    setIsToggleSideBarButtonVisible(false);
    onNavChangeHandler();
  };

  const renderDashboardButton = (dashboard: FleetDashboardModel, isEditable: boolean) => (
    <NavigationButton
      key={dashboard.id}
      isCurrentPage={queryDashboardId === dashboard.id}
      isNavOpen={isExpanded}
      title={dashboard.name}
      onClick={() => dashboardClickHandler(dashboard.id)}
      icon={<CustomFleetDashboardIcon icon={dashboard.icon} />}
      contextMenuOptions={
        isEditable
          ? [
              {
                title: t('button-edit'),
                icon: <EditIcon color="action" />,
                action: () => editDashboardClickHandler(dashboard.id),
              },
            ]
          : undefined
      }
    />
  );

  return (
    <Box
      position="relative"
      minWidth="min-content"
      sx={{ px: 1, pb: { xs: 0, sm: 1 } }}
      onMouseEnter={mouseOverNavHandler}
      onMouseLeave={mouseOffNavHandler}
      data-testid="fleet-nav-bar"
    >
      {!matchesSmDown && (
        <Box
          sx={(theme) => ({
            position: 'absolute',
            top: theme.spacing(4.5),
            right: theme.spacing(-2),
            borderRadius: '50%',
            zIndex: theme.zIndex.fab,
          })}
        >
          <Zoom in={isToggleSideBarButtonVisible} timeout={100} unmountOnExit>
            <Fab
              onClick={toggleSideBarClickHandler}
              size="small"
              data-testid="toggle-fleet-nav-bar"
              sx={(theme) => ({
                boxShadow: 2,
                background: theme.palette.background.default,
                ':hover': { backgroundColor: theme.palette.action.hover },
              })}
            >
              {isNavOpen ? <CloseIcon /> : <OpenIcon />}
            </Fab>
          </Zoom>
        </Box>
      )}
      <Stack
        data-testid="fleet-nav-bar-container"
        sx={(theme) => ({
          transition: 'all 0.4s ease-in-out',
          overflowX: 'hidden',
          overflowY: matchesSmDown ? 'visible' : 'auto',
          width: { xs: '100%', sm: isExpanded ? theme.spacing(32) : theme.spacing(7) },
          height: matchesSmDown ? 'auto' : '100%',
        })}
      >
        {builtInPages.map((builtInPage) => (
          <NavigationButton
            key={builtInPage.page}
            isCurrentPage={activeGenericPage === builtInPage.page}
            isNavOpen={isExpanded}
            title={t(builtInPage.titleKey)}
            onClick={() => genericPageClickHandler(builtInPage.page)}
            icon={<CustomFleetDashboardIcon icon={builtInPage.icon} />}
          />
        ))}

        {isManufacturer && defaultOrbitDashboards.length > 0 && <Divider sx={{ my: 1 }} />}
        {isManufacturer && defaultOrbitDashboards.map((d) => renderDashboardButton(d, false))}

        {((canSeeDealerOverview && internalDashboards.length > 0) ||
          dealerDashboards.length > 0 ||
          changeDashboards.length > 0 ||
          (isManufacturer && personalOrbitDashboards.length > 0)) && <Divider sx={{ my: 1 }} />}

        {/* Only the manufacturer saves and edits views; the others may still open their own. */}
        {canSeeDealerOverview &&
          internalDashboards.map((d) => renderDashboardButton(d, isManufacturer))}
        {dealerDashboards.map((d) => renderDashboardButton(d, isManufacturer))}
        {changeDashboards.map((d) => renderDashboardButton(d, isManufacturer))}
        {isManufacturer && personalOrbitDashboards.map((d) => renderDashboardButton(d, false))}

        {isManufacturer && (
          <React.Fragment>
            <Divider sx={{ my: 1 }} />
            <NavigationButton
              isCurrentPage={isDatasetPage}
              isNavOpen={isExpanded}
              title={t('title-datasets-and-filters')}
              onClick={datasetsClickHandler}
              icon={<DatasetConfigIcon />}
            />
          </React.Fragment>
        )}
      </Stack>
    </Box>
  );
};

export default FleetNavigationBar;
